/**
 * In-memory access-token store (POD1-8).
 *
 * The access token is deliberately NOT in localStorage or sessionStorage. Both
 * are readable by any script on the page, so one XSS anywhere in the app hands
 * an attacker a working credential. Holding it in a module-scoped variable
 * means it dies with the tab.
 *
 * The refresh token is never here at all - it lives in the HttpOnly
 * `sale4all_refresh` cookie the backend sets, which JavaScript cannot read.
 *
 * Losing the access token on reload is the intended behaviour: bootstrap calls
 * the refresh endpoint, the browser sends the cookie automatically, and a new
 * access token comes back.
 *
 * Implemented as a plain module rather than an Angular service on purpose -
 * `ApiUrl` and the interceptor both need it, and a module-scoped value avoids
 * changing any constructor signature.
 */

/** Non-credential marker so UI "am I logged in?" checks keep working. */
export const LOGGED_IN_KEY = 'is_logged_in';

let accessToken: string | null = null;

export const TokenStore = {
  set(token: string | null): void {
    accessToken = token || null;
    try {
      if (accessToken) {
        localStorage.setItem(LOGGED_IN_KEY, '1');
      } else {
        localStorage.removeItem(LOGGED_IN_KEY);
      }
    } catch (e) {
      // Private mode / storage disabled - the in-memory token still works.
    }
  },

  get(): string | null {
    return accessToken;
  },

  /** True when an access token is held right now. */
  hasToken(): boolean {
    return !!accessToken;
  },

  /**
   * True when the user is believed to be signed in.
   *
   * Survives reload (the marker is persisted) even though the access token
   * does not, so the UI does not flash "logged out" during the bootstrap
   * refresh. It is only a hint: the server is the authority.
   */
  isLoggedIn(): boolean {
    if (accessToken) return true;
    try {
      return !!localStorage.getItem(LOGGED_IN_KEY);
    } catch (e) {
      return false;
    }
  },

  clear(): void {
    accessToken = null;
    try {
      localStorage.removeItem(LOGGED_IN_KEY);
    } catch (e) {
      // ignore
    }
  },
};
